/**
 * Discover converter-emitted terrain derivative layers in the catalog and
 * resolve them as **companions / alternates** of a terrain layer (RFC-108-G
 * §2.1–§2.4 — but the same shape regardless of whether the SceneItems carry
 * `alternateOfLayerId` yet).
 *
 * Owns the **only** policy for "which catalog row backs which terrain tool":
 *
 *   - hillshade (`terrain-derived-hillshade` COG → GPU-friendly raster overlay)
 *   - slope     (`terrain-derived-slope` COG)
 *   - aspect    (`terrain-derived-aspect` COG)
 *   - ndsm      (`terrain-derived-ndsm` COG, paired DSM-DTM)
 *   - contours  (`dem-contours` PMTiles vector)
 *
 * G20 in `LAYER_COMPOSITION_AND_ACTIVATION.md` §6: the terrain tool card was
 * dead on DEM projects because the base render is a colormapped ColorLayer
 * (DEVELOPMENT_WALL §2 decision — "ColorLayer not ElevationLayer"). The tools
 * need elevation DATA: either an ElevationLayer (parked) or these derived
 * products as RFC-108-G alternates / companion overlays. This module is the
 * one place that decides "is the data prerequisite present".
 *
 * Discovery is conservative on purpose (operator-visible side-effect):
 *
 *   1. Explicit converter tag: `style.terrainDerivative === '<role>'`
 *      (preferred — converter writes it).
 *   2. Explicit RFC-108-G alternate: `alternateOfLayerId === terrain.id` AND
 *      `alternateLabel ~ /hillshade|slope|aspect|ndsm|contour/i`.
 *   3. `derivedFrom` includes the terrain layer id AND id/label/intakeKind
 *      contains the role keyword.
 *   4. `style.intakeKind === 'dem-contours'` AND `derivedFrom` includes the
 *      terrain layer id → contours.
 *
 * Pure module — no React, no I/O.
 */

import type { Layer } from '@geosoft/viewer-sdk';

export type TerrainDerivativeRole = 'hillshade' | 'slope' | 'aspect' | 'ndsm' | 'contours';

export interface TerrainDerivatives {
  hillshade: Layer | null;
  slope: Layer | null;
  aspect: Layer | null;
  ndsm: Layer | null;
  contours: Layer | null;
}

export const EMPTY_TERRAIN_DERIVATIVES: TerrainDerivatives = {
  hillshade: null,
  slope: null,
  aspect: null,
  ndsm: null,
  contours: null,
};

const ROLE_KEYWORDS: Readonly<Record<TerrainDerivativeRole, readonly string[]>> = {
  hillshade: ['hillshade', 'hill-shade'],
  slope: ['slope'],
  aspect: ['aspect'],
  ndsm: ['ndsm', 'n-dsm', 'normalised-dsm', 'normalized-dsm'],
  contours: ['contour', 'contours'],
};

function styleValue(layer: Layer, key: string): unknown {
  if (!layer.style || typeof layer.style !== 'object') return undefined;
  return (layer.style as Record<string, unknown>)[key];
}

function styleString(layer: Layer, key: string): string | undefined {
  const value = styleValue(layer, key);
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function derivedFromIncludes(layer: Layer, parentId: string): boolean {
  return Array.isArray(layer.derivedFrom) && layer.derivedFrom.includes(parentId);
}

function textContains(text: string, keywords: readonly string[]): boolean {
  const lower = text.toLowerCase();
  return keywords.some((k) => lower.includes(k));
}

function isRasterCog(layer: Layer): boolean {
  if (layer.kind !== 'raster' && layer.kind !== 'terrain') return false;
  const intake = styleString(layer, 'intakeKind') ?? '';
  return intake === 'cog' || layer.endpoints.tiles?.includes('/cog/') === true;
}

function isVectorContours(layer: Layer): boolean {
  if (layer.kind !== 'vector') return false;
  const intake = styleString(layer, 'intakeKind') ?? '';
  if (intake === 'dem-contours') return true;
  if (intake === 'pmtiles-vector' || intake === 'pmtiles' || intake === 'flatgeobuf') {
    const text = `${layer.id} ${layer.label ?? ''}`;
    return textContains(text, ROLE_KEYWORDS.contours);
  }
  return false;
}

/**
 * Decide whether `candidate` is the role's derivative for the given
 * `terrain` parent. Returns the role string or `null`.
 */
function classifyDerivative(
  candidate: Layer,
  terrain: Layer,
): TerrainDerivativeRole | null {
  if (candidate.id === terrain.id) return null;

  // (1) Explicit converter tag or `artifactKind` spelling.
  const declared = resolveTerrainDerivativeRole(candidate);
  if (declared) return declared;

  // (2) RFC-108-G alternate keyed to this terrain id.
  if (candidate.alternateOfLayerId === terrain.id) {
    const text = `${candidate.alternateLabel ?? ''} ${candidate.id} ${candidate.label ?? ''}`;
    for (const role of Object.keys(ROLE_KEYWORDS) as TerrainDerivativeRole[]) {
      if (textContains(text, ROLE_KEYWORDS[role])) return role;
    }
  }

  // (3) derivedFrom + role keyword in id/label.
  if (derivedFromIncludes(candidate, terrain.id)) {
    const text = `${candidate.id} ${candidate.label ?? ''}`;
    for (const role of Object.keys(ROLE_KEYWORDS) as TerrainDerivativeRole[]) {
      if (textContains(text, ROLE_KEYWORDS[role])) {
        // Hillshade/slope/aspect/ndsm are raster COGs; contours is vector.
        if (role === 'contours' && !isVectorContours(candidate)) continue;
        if (role !== 'contours' && !isRasterCog(candidate)) continue;
        return role;
      }
    }
  }

  // (4) `dem-contours` PMTiles whose `derivedFrom` references this terrain.
  if (isVectorContours(candidate) && derivedFromIncludes(candidate, terrain.id)) {
    return 'contours';
  }

  return null;
}

export function resolveTerrainDerivatives(
  terrain: Layer | null | undefined,
  catalog: readonly Layer[] | null | undefined,
): TerrainDerivatives {
  // Parent may be `kind: 'terrain'` OR a dem-like raster COG (converter often
  // registers the elevation surface as realityData/raster, not terrain).
  if (
    !terrain
    || (terrain.kind !== 'terrain' && terrain.kind !== 'raster')
    || !catalog
    || catalog.length === 0
  ) {
    return { ...EMPTY_TERRAIN_DERIVATIVES };
  }
  if (terrain.kind === 'raster' && isTerrainDerivativeLayer(terrain)) {
    return { ...EMPTY_TERRAIN_DERIVATIVES };
  }

  const out: TerrainDerivatives = { ...EMPTY_TERRAIN_DERIVATIVES };
  for (const candidate of catalog) {
    const role = classifyDerivative(candidate, terrain);
    if (!role) continue;
    // Keep the first match per role (tagged > alternate > derivedFrom > vector).
    if (out[role] === null) {
      out[role] = candidate;
    }
  }
  return out;
}

/**
 * Convenience predicate for the tool card: does the terrain layer have ANY
 * derivative companion?  Used to display a single "no derived terrain data"
 * callout instead of five disabled rows screaming individually.
 */
export function hasAnyTerrainDerivative(derivatives: TerrainDerivatives): boolean {
  return Object.values(derivatives).some((layer) => layer !== null);
}

/**
 * Canonical role of a derived terrain product, from either spelling the
 * converter may emit: the `style.terrainDerivative` tag (preferred) or an
 * `artifactKind` like `terrain-hillshade-cog`.
 *
 * This is the ONE place that maps a catalog row / SceneItem onto a role.
 * Consumers that disagree produce the classic stacked-companion bug: the
 * activation policy leaves hillshade/slope/aspect at default tier (all four
 * COGs visible at once, exactly overlapping), while the engines fall back to
 * the generic DEM gray stretch with no nodata mask — so each companion paints
 * an opaque rectangle over its footprint and hiding the top one just uncovers
 * the next one's rectangle ("the layer left its background behind").
 */
export function resolveTerrainDerivativeRole(
  input: Pick<Layer, 'style'> & { readonly id?: string; readonly label?: string | null },
): TerrainDerivativeRole | null {
  const layer = input as Layer;
  const tagged = styleString(layer, 'terrainDerivative');
  if (tagged && tagged in ROLE_KEYWORDS) return tagged as TerrainDerivativeRole;

  const artifact = (
    styleString(layer, 'gtwinArtifactKind')
    ?? styleString(layer, 'artifactKind')
    ?? ''
  ).toLowerCase();
  if (artifact.startsWith('terrain-') || artifact.includes('contour')) {
    for (const role of Object.keys(ROLE_KEYWORDS) as TerrainDerivativeRole[]) {
      if (textContains(artifact, ROLE_KEYWORDS[role])) return role;
    }
  }

  if (styleString(layer, 'intakeKind') === 'dem-contours') return 'contours';
  return null;
}

/**
 * Converter-tagged hillshade / slope / aspect / contours companions.
 * These must NOT all start visible — stacking them on the DEM looks like
 * TV static until the operator hides extras.
 */
export function isTerrainDerivativeLayer(layer: Pick<Layer, 'id' | 'label' | 'style' | 'kind'>): boolean {
  return resolveTerrainDerivativeRole(layer as Layer) !== null;
}

/**
 * Primary elevation surface used to discover companions. Accepts `kind:
 * 'terrain'` or a dem-like `raster` COG that is not itself a derivative.
 */
export function findTerrainAnalysisParent(
  catalog: readonly Layer[] | null | undefined,
): Layer | null {
  if (!catalog || catalog.length === 0) return null;
  const terrain = catalog.find((l) => l.kind === 'terrain');
  if (terrain) return terrain;
  return (
    catalog.find((l) => {
      if (l.kind !== 'raster') return false;
      if (isTerrainDerivativeLayer(l)) return false;
      const text = `${l.id} ${l.label ?? ''} ${styleString(l, 'artifactKind') ?? ''}`.toLowerCase();
      if (/(dem|dtm|dsm|elevation|rastercalc|hypsometr)/.test(text)) return true;
      const artifact = (styleString(l, 'gtwinArtifactKind') ?? styleString(l, 'artifactKind') ?? '').toLowerCase();
      return artifact === 'cog' || artifact === 'dem' || artifact === 'terrain-cog';
    }) ?? null
  );
}
